import { CheckIcon, MultiplyIcon, TelegramAltIcon } from "@/components/Icons"
import { Modal } from "@/components/Modal"
import { P3 } from "@/components/Typographies"
import { SHARED_DATA_STATUS, SHARED_DATA_TYPE } from "@/domain/sharedData/constants"
import { getSharedEntityType } from "@/domain/sharedData/getSharedUtils"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import useGetUserRoles from "@/hooks/useGetUserRoles"
import type { SharedDataJsonldReadCollectionSharedData } from "@/networking/api"
import { isNotNullNorUndefined } from "@/utils/ts-utils"
import {
  Box,
  Button,
  Flex,
  FormControl,
  Grid,
  GridItem,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react"
import { FormattedMessage, useIntl } from "react-intl"
import { SharedDataStatusTag } from "../DomainTag/SharedDataStatusTag"
import { CommentSection } from "./sections/CommentSection"
import { EndSection } from "./sections/EndSection"
import { InterBUSection } from "./sections/InterBUSection"
import { PeopleSection } from "./sections/PeopleSection"
import { ProjectSection } from "./sections/ProjectSection"
import { ReceiverManagerSection } from "./sections/ReceiverManagerSection"
import { ReceiverSection } from "./sections/ReceiverSection"
import { RequestSection } from "./sections/RequestSection"
import { RequestorManagerSection } from "./sections/RequestorManagerSection"
import { RequestorSection } from "./sections/RequestorSection"

type Props = {
  isOpen: boolean
  onClose: () => void
  onOpenAccept: () => void
  onOpenDecline: () => void
  onOpenRevoke: () => void
  onOpenCancel: () => void
  onOpenCancelShare: () => void
  onOpenEndOk: () => void
  onOpenEndKo: () => void
  onDuplicate: () => void
  refreshQueries?: () => void
  sharedEntity: SharedDataJsonldReadCollectionSharedData
  fromOrTo: "from" | "to"
}

export default function ShareResponseModal({
  isOpen,
  onClose,
  onOpenAccept,
  onOpenDecline,
  onOpenRevoke,
  onOpenCancel,
  onOpenCancelShare,
  onOpenEndOk,
  onOpenEndKo,
  onDuplicate,
  refreshQueries,
  sharedEntity,
  fromOrTo,
}: Props) {
  const intl = useIntl()

  const { clusterIds } = useGetUserInfos()
  const isUserRequestor = fromOrTo === "to" // && clusterIds.includes(sharedEntity.fromCluster?.id ?? "")

  const { hasRoleBuAdmin } = useGetUserRoles()
  const canAnswer = hasRoleBuAdmin(sharedEntity.toCluster?.id)

  const entityType = getSharedEntityType(sharedEntity)
  const canComment =
    sharedEntity.status?.code === SHARED_DATA_STATUS.ACCEPTED &&
    (clusterIds.includes(sharedEntity.fromCluster?.id ?? "") ||
      clusterIds.includes(sharedEntity.toCluster?.id ?? ""))

  const displayResult = (
    [SHARED_DATA_STATUS.CLOSED, SHARED_DATA_STATUS.COMPLETED] as string[]
  ).includes(sharedEntity.status?.code ?? "")

  const displayActions = !(
    [SHARED_DATA_STATUS.CLOSED, SHARED_DATA_STATUS.COMPLETED] as string[]
  ).includes(sharedEntity.interBu?.status?.code ?? "")

  const onRefresh = () => {
    refreshQueries?.()
  }
  const onAccept = () => {
    onOpenAccept()
    onClose()
  }
  const onDecline = () => {
    onOpenDecline()
    onClose()
  }
  const onRevoke = () => {
    onOpenRevoke()
    onClose()
  }
  const onCancel = () => {
    onOpenCancel()
    onClose()
  }
  const onCancelShare = () => {
    onOpenCancelShare()
    onClose()
  }
  const onEndOk = () => {
    onOpenEndOk()
    onClose()
  }
  const onEndKo = () => {
    onOpenEndKo()
    onClose()
  }
  const onCancelledRevoke = () => {
    if (!confirm(intl.formatMessage({ id: "userSharedData.cancelRevoke.confirm" }))) return
    onOpenAccept()
    onClose()
  }

  return (
    <Modal
      {...{ isOpen, onClose }}
      title={<FormattedMessage id={`userSharedData.${entityType}.modal.title2`} />}
      scrollBehavior="outside"
      // Wider: the sections use the sheet layout (name in a margin, fields in a grid).
      size="4xl"
    >
      <ModalBody>
        <FormControl as={Flex} flexDir="column">
          {isNotNullNorUndefined(sharedEntity.people) && (
            <PeopleSection people={sharedEntity.people} />
          )}
          {isNotNullNorUndefined(sharedEntity.project) && (
            <ProjectSection project={sharedEntity.project} />
          )}
          {isNotNullNorUndefined(sharedEntity.interBu) && (
            <InterBUSection interBu={sharedEntity.interBu} />
          )}
          {isUserRequestor ? (
            <>
              <ReceiverSection
                collaborator={sharedEntity.toManager}
                cluster={sharedEntity.toCluster}
                onDuplicate={onDuplicate}
              />
              {isNotNullNorUndefined(sharedEntity.interBu) && (
                <RequestorManagerSection sharedEntity={sharedEntity} refreshQueries={onRefresh} />
              )}
            </>
          ) : (
            <>
              <RequestorSection
                collaborator={sharedEntity.from}
                cluster={sharedEntity.fromCluster}
              />
              {isNotNullNorUndefined(sharedEntity.interBu) && (
                <ReceiverManagerSection sharedEntity={sharedEntity} refreshQueries={onRefresh} />
              )}
            </>
          )}
          <RequestSection entity={sharedEntity} />
          {displayResult && <EndSection entity={sharedEntity} />}
          {/* The thread is shown as soon as there are comments; writing keeps its former rule. */}
          {(canComment || (sharedEntity.interBu?.comments ?? []).length > 0) && (
            <CommentSection
              entity={sharedEntity}
              refreshQueries={onRefresh}
              canWrite={canComment}
            />
          )}
        </FormControl>
      </ModalBody>

      <ModalFooter>
        {displayActions &&
          isUserRequestor &&
          sharedEntity.status?.code === SHARED_DATA_STATUS.ASKED && (
            <ButtonCancelShare onClick={onCancelShare} />
          )}
        {displayActions && canAnswer && sharedEntity.status?.code === SHARED_DATA_STATUS.ASKED && (
          <>
            <ButtonDecline onClick={onDecline} />
            <ButtonAccept onClick={onAccept} />
          </>
        )}
        {displayActions &&
          canAnswer &&
          sharedEntity.status?.code === SHARED_DATA_STATUS.ACCEPTED &&
          (entityType === SHARED_DATA_TYPE.INTER_BU ? (
            <Grid w="100%" gap={2} templateColumns="repeat(3, 1fr)">
              <GridItem colSpan={1}>
                {/* <ButtonRevoke onClick={onRevoke} /> */}
                <ButtonCancel onClick={onCancel} />
              </GridItem>
              <GridItem colSpan={2} display="flex" flexDir="row" gap={2} justifyContent="end">
                <ButtonEndKo onClick={onEndKo} />
                <ButtonEndOk onClick={onEndOk} />
              </GridItem>
            </Grid>
          ) : (
            <ButtonRevoke onClick={onRevoke} />
          ))}
        {!displayResult &&
          (!canAnswer ||
            (sharedEntity.status?.code !== SHARED_DATA_STATUS.ASKED &&
              sharedEntity.status?.code !== SHARED_DATA_STATUS.ACCEPTED)) && (
            <>
              <Box display="block">
                <P3>
                  <FormattedMessage id="sharing.sharedItem.status" /> :
                </P3>
                <SharedDataStatusTag status={sharedEntity.status?.code} pl={2} ml={2} />
              </Box>
              {displayActions &&
                canAnswer &&
                (sharedEntity.status?.code === SHARED_DATA_STATUS.DENIED ||
                  sharedEntity.status?.code === SHARED_DATA_STATUS.REVOKED) && (
                  <Box display="block">
                    <ButtonCancelRevoke onClick={onCancelledRevoke} />
                  </Box>
                )}
            </>
          )}
      </ModalFooter>
    </Modal>
  )
}

const ButtonDecline = ({ onClick }: { onClick: () => void }) => (
  <Button display="flex" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id="userSharedData.declineRequest" />
    <TelegramAltIcon boxSize={4} />
  </Button>
)

const ButtonAccept = ({ onClick }: { onClick: () => void }) => (
  <Button display="flex" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id="userSharedData.acceptRequest" />
    <TelegramAltIcon boxSize={4} />
  </Button>
)

const ButtonCancelShare = ({ onClick }: { onClick: () => void }) => (
  <Button size="sm" display="flex" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id="userSharedData.cancelShare" />
    <MultiplyIcon boxSize={4} />
  </Button>
)

const ButtonCancel = ({ onClick }: { onClick: () => void }) => (
  <Button display="flex" variant="outline" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id="userSharedData.cancel" />
    <MultiplyIcon boxSize={4} />
  </Button>
)

const ButtonEndKo = ({ onClick }: { onClick: () => void }) => (
  <Button display="flex" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id="userSharedData.end_ko" />
    <MultiplyIcon boxSize={4} />
  </Button>
)

const ButtonEndOk = ({ onClick }: { onClick: () => void }) => (
  <Button display="flex" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id="userSharedData.end_ok" />
    <CheckIcon boxSize={4} />
  </Button>
)

const ButtonRevoke = ({ onClick, isCancel }: { onClick: () => void; isCancel?: boolean }) => (
  <Button display="flex" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id={isCancel ? "userSharedData.cancel" : "userSharedData.revokeRequest"} />
    <MultiplyIcon boxSize={4} />
  </Button>
)

const ButtonCancelRevoke = ({ onClick }: { onClick: () => void }) => (
  <Button size="sm" display="flex" gap="2" type="button" onClick={() => onClick()}>
    <FormattedMessage id="userSharedData.cancelRevoke" />
  </Button>
)
